import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { RemovableRef, useLocalStorage } from '@vueuse/core';
import type { EnrollmentDTO } from "@tmi/types/api/user-notifications";
import { getAllUserEnrollments } from "@/services/user-notifications-service";

import type {
    ApiFetchResponse,
    KeyValue,
    GraphQlPayload,
    OpraAgreementPayload,
    RecurlyHostedLink,
    TenantContextPayload,
    UpdateUserResponse,
    PersistedQueryPayload
} from "@tmi/types/api/fetch";
import { goFetch, isSuccessState } from "@/utils/network-utils";

// Logging dependencies
import { createDefaultLogger } from "@/types/console-logger";
import { clientStateManager } from "@/services/error-services";
const logger = createDefaultLogger();

import { useSectorsStore } from "@/stores/Sectors";
import { Sector } from "@tmi/types/entities";

import {
    FavoriteArticle,
    FavoriteSector,
    FavoriteWebinar,
    RemovedFavoriteArticle,
    RemovedFavoriteSector,
    RemovedFavoriteWebinar
} from "@tmi/types/api/content-models";

import { articleKey, webinarKey } from "@/types/products/product-definitions";

// Type dependencies
import type { UserProfileDataApiResponse, UserProfileInfoApiResponse } from "@tmi/types/api/user";
import { UserInfo, ServiceAccessParameters } from "@tmi/types/user";
import { AUTH_RESET_EVENT, useAuthStore } from "@/stores/Auth";
import { LabelService, useLabelService } from "@/composables/label-service";

const defaultUser: UserInfo = {
    id: -1,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dmiServiceAgreementSigned: undefined,
    dmiServiceAccessParameters: undefined
}

const error = ref< string >( '' );

const user = useLocalStorage< UserInfo >( 'user', defaultUser );
const favoriteArticles = useLocalStorage< string[] >( 'fav_art', [] );
const favoriteSectors = useLocalStorage< number[] >( 'fav_sec', [] );
const favoriteWebinars = useLocalStorage< string[] >( 'fav_web', [] );
const recurlyProfileLink = useLocalStorage< string >( 'recurly_profile_link', '' );
const notificationEnrollments = useLocalStorage< EnrollmentDTO[] >('notification_enrollments', [] );

const initialized = computed< boolean >( () => user.value.id > 0 );
// UX roadmap #78: the dashboard's favourite-sector card asks a member who has starred none to star some
const hasFavoriteSectors = computed< boolean >( () => ( favoriteSectors.value?.length ?? 0 ) > 0 );
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
/** The profile request in flight. Every caller joins it, so the permissions heal never runs beside a sign-in's load (F44). */
let profileRequest: Promise< void > | null = null;

const TEMP_PB_ON_DASHBOARD_FLAG_LOCALSTORAGE_KEY: string = 'pb_dashboard';
const portfolioBuilderOnDashboard: RemovableRef< boolean > = useLocalStorage( TEMP_PB_ON_DASHBOARD_FLAG_LOCALSTORAGE_KEY, false );

export type SectorColumnMeta = {
    field: string,
    header: string
}

const labelService: LabelService = useLabelService();

export const mandatoryColumns = [
    { field: 'rank', header: `${ labelService.label( 'lblRsProxy' ) } Rank` },
    { field: 'name', header: 'Sector' }
] as SectorColumnMeta[];

export const toggleableColumns =  ref< SectorColumnMeta[] >( [
    { field: 'rs_col', header: labelService.label( 'lblSectorRsColumnTitle' ) },
    { field: 'bpi_sig', header: labelService.label( 'lblSectorBpiSignalTitle' ) },
    { field: 'bpi_col', header: labelService.label( 'lblSectorBpiColumnTitle' ) },
    { field: 'p30_sig', header: labelService.label( 'lblSectorsP30SignalTitle' ) },
    { field: 'rs_sig', header: 'RS Sig' },
    { field: 'bpi_stat', header: 'BPI Status' },
    { field: 'bpi_val', header: 'BPI Value' },
    { field: 'p30_col', header: labelService.label( 'lblSectorsP30ColumnTitle' ) },
    { field: 'p30_val', header: '%30 Value' },
    { field: 'ew_per_chg_val', header: labelService.label( 'lblSectorsEwPercentChgTitle' ) }
] );

const defaultColumns = toggleableColumns.value.slice( 0, 4 );
const sectorColumns = useLocalStorage< SectorColumnMeta[] >( 'sector-list', defaultColumns );

/**
 * Reset every user-scoped ref to its uninitialized value. Shared by the store's `reset()` and the
 * LD1 coherence listener so the two can never diverge.
 */
function resetUserState(): void {
    // NOTE: when resetting anything that is backed by localstorage, you must first set it to null...
    user.value = null;
    favoriteArticles.value = null;
    favoriteSectors.value = null;
    favoriteWebinars.value = null;
    sectorColumns.value = null;
    notificationEnrollments.value = null;

    error.value = '';
    loading.value = false;
    // A request started for the previous session must not be joined by the next one.
    profileRequest = null;

    // ...then set it to its "uninitialized" value
    user.value = defaultUser;
    favoriteArticles.value = [];
    favoriteSectors.value = [];
    favoriteWebinars.value = [];
    recurlyProfileLink.value = '';
    notificationEnrollments.value = [];
}

// LD1 coherence: Auth.reset() purges localStorage natively (not observed by same-document VueUse
// refs) and dispatches this event so the in-memory profile dies with the token.
if ( typeof window !== 'undefined' ) {
    window.addEventListener( AUTH_RESET_EVENT, resetUserState );
}

export const useUserStore = defineStore( "user-setup", () => {
    const { authToken } = storeToRefs( useAuthStore() );
    const sectorStore = useSectorsStore();

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        resetUserState();
    }

    /**
     * The profile, then the account details. UX roadmap #24: OPRA status and notification enrollments need only the
     * token, so they load side by side once the profile is in; before, the four requests (7 s timeout each) ran one
     * after another. Never rejects.
     */
    async function initializeAsync(): Promise< void > {
        if ( initialized.value ) {
            return;
        }

        await initializeProfileAsync();

        // A failed profile load skips the rest, as before.
        if ( initialized.value ) {
            await loadAccountDetailsAsync();
        }
    }

    /**
     * Lean profile-only load used by the permissions self-heal (tmi-6gh, LD2) and by sign-in, which fetches the grants
     * (they need the userId) beside the account details. A load already in flight is joined rather than skipped, so
     * the caller always sees its result (F44). Never rejects.
     */
    function initializeProfileAsync(): Promise< void > {
        if ( initialized.value ) {
            return Promise.resolve();
        }

        if ( !profileRequest ) {
            loading.value = true;
            const request: Promise< void > = loadUserProfileAsync()
                .catch( () => { /* the caller reads `initialized` / userId() */ } )
                .finally( () => {
                    if ( profileRequest === request ) {
                        profileRequest = null;
                        loading.value = false;
                    }
                } );
            profileRequest = request;
        }

        return profileRequest;
    }

    /**
     * UX roadmap #24: what follows the profile, side by side: OPRA status (the scanner page reads it once, on mount)
     * and the notification enrollments, awaited; the Recurly billing link, not awaited (the billing button reads it
     * when clicked). Never rejects.
     */
    async function loadAccountDetailsAsync(): Promise< void > {
        void loadRecurlyLink();
        await Promise.allSettled( [
            loadOpraStatusAsync(),
            loadNotificationEnrollmentsAsync()
        ] );
    }

    async function loadUserProfileAsync() {
        const response =
            await goFetch< UserProfileDataApiResponse, PersistedQueryPayload >( {
                endpoint: 'user/graphql',
                payload: {
                    id: '68a21a414d226e94617a44a605468c06',
                    variables: {}
                },
                authToken: authToken.value
            } );

        if ( response?.data?.user ) {
            const userDataResponse: UserProfileInfoApiResponse = response.data.user;
            if ( userDataResponse ) {
                user.value = {
                    id: userDataResponse.id,
                    firstName:  userDataResponse.firstName,
                    lastName:  userDataResponse.lastName,
                    email:  userDataResponse.email,
                    phone:  userDataResponse.phone,
                    dmiServiceAgreementSigned: undefined,
                    dmiServiceAccessParameters: undefined
                };

                favoriteArticles.value = userDataResponse.favoriteArticles ? userDataResponse.favoriteArticles.map( a => a.articleId ) : [];
                favoriteSectors.value = userDataResponse.favoriteSectors ? userDataResponse.favoriteSectors.map( s => s.sectorId ) : [];
                favoriteWebinars.value = userDataResponse.favoriteWebinars ? userDataResponse.favoriteWebinars.map( w => w.webinarId ) : [];
            }
        }
    }

    async function loadOpraStatusAsync(): Promise< void > {
        const response =
            await goFetch< ServiceAccessParameters, { serviceName: string } >( {
                endpoint: `registrar/service/access/parameters`,
                authToken: authToken.value,
                payload: { serviceName: 'DMI Scanner' }
            } );

        if ( response?.data && isSuccessState( response.status ) ) {
            user.value.dmiServiceAgreementSigned = response.data.enrollmentVerified;
            user.value.dmiServiceAccessParameters = response.data
        }
    }

    async function loadRecurlyLink(): Promise< void > {
        goFetch< RecurlyHostedLink, TenantContextPayload >( {
            endpoint: `authentication/initiateauth`,
            authToken: authToken.value,
            payload: {
                identityProviderId: 'ef647525-803b-4a52-b71b-fc9b271b496e',
                secrets: []
            }
        } )
        .then( ( linkData: ApiFetchResponse< RecurlyHostedLink > | null ) => {
            recurlyProfileLink.value = linkData?.data?.authResult?.token || undefined;
        } );
    }

    /**
     * Refreshes the SMS enrollments. A failed request keeps the last list: it used to store `undefined`, and the SMS
     * widget threw on it until the next reload (forms-15). Never rejects; true when a fresh list landed.
     */
    async function loadNotificationEnrollmentsAsync(): Promise< boolean > {
        try {
            const enrollments = await getAllUserEnrollments( authToken.value );
            if ( enrollments ) {
                notificationEnrollments.value = enrollments;
                return true;
            }
        } catch ( err: unknown ) {
            clientStateManager.queueClientError( err, { request: 'usernotification/getUserEnrollments' } );
        }

        notificationEnrollments.value = notificationEnrollments.value ?? [];
        return false;
    }

    const hasSignedOpraAgreement = computed< boolean >( () => user.value.dmiServiceAgreementSigned ?? false );
    const scannerCredentials = computed< ServiceAccessParameters | undefined >( () =>
        hasSignedOpraAgreement.value ? user.value.dmiServiceAccessParameters : undefined
    );

    const userFullName = computed< string >( () => {
        const first: string = user.value?.firstName ?? '';
        const last: string = user.value?.lastName || '';

        let fullName: string = `${ first } ${ last }`;
        if ( fullName.trim().length === 0 ) {
            fullName = user.value?.email ?? '---';
        }

        return fullName;
    } );

    function userId(): number | null {
        return user.value.id ?? null;
    }

    function isFavorited( contentId: string, contentType: string ) {
        switch ( contentType ) {
            case articleKey:
                return isFavoriteArticle( contentId );
            case webinarKey:
                return isFavoriteWebinar( contentId );
        }

        return false;
    }

    function isFavoriteArticle( articleId: string ): boolean {
        return favoriteArticles.value.includes( articleId );
    }

    function isFavoriteSector( sectorKey: number | string ): boolean {
        let sectorId: number = -1;
        if ( typeof sectorKey === 'string' ) {
            const sector: Sector | undefined = sectorStore.sectorBySymbol( sectorKey );
            if ( sector ) {
                sectorId = sector.id;
            }
        } else {
            sectorId = sectorKey;
        }

        return favoriteSectors.value.includes( sectorId );
    }

    function isFavoriteWebinar( webinarId: string ): boolean {
        return favoriteWebinars.value.indexOf( webinarId ) > -1;
    }

    function toggleContentFavoriteStatus( contentId: string, contentType: string, responseCallback: ( result: boolean ) => void ) {
        switch ( contentType ) {
            case articleKey:
                toggleArticleFavoriteStatus( contentId, responseCallback );
                return;
            case webinarKey:
                toggleWebinarFavoriteStatus( contentId, responseCallback );
                return;
            default:
                return;
        }
    }

    function togglePortfolioBuilderOnDashboard( responseCallback: ( result: boolean ) => void ) {
        portfolioBuilderOnDashboard.value = !portfolioBuilderOnDashboard.value;
        responseCallback( portfolioBuilderOnDashboard.value );
    }

    function toggleSectorFavoriteStatus( sector: Sector, responseCallback: ( result: boolean ) => void ) {
        loading.value = true;
        const sectorId: number = sector.id;

        logger.debug( `toggleSectorFavoriteStatus(): sector id ${ sectorId }` );

        // If the sector was already a favorite, then remove it from the list of favorites
        if ( isFavoriteSector( sectorId ) ) {
            logger.debug( `toggleSectorFavoriteStatus(): was a favorite, attempting to remove` );

            goFetch< RemovedFavoriteSector, GraphQlPayload >( {
                endpoint: `user/graphql`,
                authToken: authToken.value,
                payload: {
                    query: `mutation DeleteFavoriteSector( $sectorId: Int! ) { deleteFavoriteSector( sectorId: $sectorId ) }`,
                    variables: {
                        sectorId: sectorId
                    }
                }
            } ).then( ( response: ApiFetchResponse< RemovedFavoriteSector > | null ) => {
                // Update the local representation only if there are no errors and the id was returned as confirmation
                if ( isSuccessState( response?.status ) && response?.data ) {
                    const indexOfSector: number = favoriteSectors.value.indexOf( sectorId );
                    favoriteSectors.value.splice( indexOfSector, 1 );
                }

                responseCallback( response?.data?.deleteFavoriteSector ?? false );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                responseCallback( false );
            } ).finally( () => loading.value = false );
        }
        // Add the sector to the list of favorite sectors
        else {
            logger.debug( `toggleSectorFavoriteStatus(): was NOT a favorite, attempting to add` );

            goFetch< FavoriteSector, GraphQlPayload >( {
                endpoint: `user/graphql`,
                authToken: authToken.value,
                payload: {
                    query: `mutation AddFavoriteSector( $sectorId: Int! ) { addFavoriteSector( sectorId: $sectorId ) { sectorId } }`,
                    variables: {
                        sectorId: sectorId
                    }
                }
            } ).then( ( response: ApiFetchResponse< FavoriteSector > | null ) => {
                // Update the local representation only if there are no errors and the id was returned as confirmation
                if ( isSuccessState( response?.status ) && response?.data ) {
                    favoriteSectors.value.push( response?.data.addFavoriteSector.sectorId );
                }

                responseCallback( !!response?.data?.addFavoriteSector?.sectorId );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                responseCallback( false );
            } ).finally( () => loading.value = false );
        }
    }

    function toggleArticleFavoriteStatus( id: string, responseCallback: ( result: boolean ) => void ) {
        loading.value = true;

        // Add to favorites
        if ( isFavoriteArticle( id ) ) {
            goFetch< RemovedFavoriteArticle, GraphQlPayload >( {
                endpoint: `user/graphql`,
                authToken: authToken.value,
                payload: {
                    query: `mutation DeleteFavoriteArticle( $articleId: String! ) { deleteFavoriteArticle( articleId: $articleId ) }`,
                    variables: {
                        articleId: id
                    }
                }
            } ).then( ( response: ApiFetchResponse< RemovedFavoriteArticle > | null ) => {
                // Update the local representation only if there are no errors and the id was returned as confirmation
                if ( isSuccessState( response?.status ) && response?.data ) {
                    // Update the local version
                    const indexOfArticle: number = favoriteArticles.value.indexOf( id );
                    favoriteArticles.value.splice( indexOfArticle, 1 );
                }

                responseCallback( response?.data?.deleteFavoriteArticle ?? false );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                responseCallback( false );
            } ).finally( () => loading.value = false );
        }
        // Remove from favorites
        else {
            goFetch< FavoriteArticle, GraphQlPayload >( {
                endpoint: `user/graphql`,
                authToken: authToken.value,
                payload: {
                    query: `mutation AddFavoriteArticle( $articleId: String! ) { addFavoriteArticle( articleId: $articleId ) { articleId } }`,
                    variables: {
                        articleId: id
                    }
                }
            } ).then( ( response: ApiFetchResponse< FavoriteArticle > | null ) => {
                // Update the local representation only if there are no errors and the id was returned as confirmation
                if ( isSuccessState( response?.status ) && response?.data ) {
                    favoriteArticles.value.push( response?.data.addFavoriteArticle.articleId );
                }

                responseCallback( !!response?.data?.addFavoriteArticle?.articleId );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                responseCallback( false );
            } ).finally( () => loading.value = false );
        }

        return false;
    }

    function toggleWebinarFavoriteStatus( id: string, responseCallback: ( result: boolean ) => void ) {
        loading.value = true;

        // Remove from favorites
        if ( isFavoriteWebinar( id ) ) {
            goFetch< RemovedFavoriteWebinar, GraphQlPayload >( {
                endpoint: `user/graphql`,
                authToken: authToken.value,
                payload: {
                    query: `mutation DeleteFavoriteWebinar( $webinarId: String! ) { deleteFavoriteWebinar( webinarId: $webinarId ) }`,
                    variables: {
                        webinarId: id
                    }
                }
            } ).then( ( response: ApiFetchResponse< RemovedFavoriteWebinar > | null ) => {
                // Update the local representation only if there are no errors and the id was returned as confirmation
                if ( isSuccessState( response?.status ) && response?.data ) {
                    // Update the local version
                    const indexOfWebinar: number = favoriteWebinars.value.indexOf( id );
                    favoriteWebinars.value.splice( indexOfWebinar, 1 );
                }

                responseCallback( response?.data?.deleteFavoriteWebinar ?? false );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                responseCallback( false );
            } ).finally( () => loading.value = false );
        }
        // Remove from favorites
        else {
            goFetch<FavoriteWebinar, GraphQlPayload >( {
                endpoint: `user/graphql`,
                authToken: authToken.value,
                payload: {
                    query: `mutation AddFavoriteWebinar( $webinarId: String! ) { addFavoriteWebinar( webinarId: $webinarId ) { webinarId } }`,
                    variables: {
                        webinarId: id
                    }
                }
            } ).then( ( response: ApiFetchResponse< FavoriteWebinar > | null ) => {
                // Update the local representation only if there are no errors and the id was returned as confirmation
                if ( isSuccessState( response?.status ) && response?.data ) {
                    favoriteWebinars.value.push( response?.data.addFavoriteWebinar.webinarId );
                }

                responseCallback( !!response?.data?.addFavoriteWebinar?.webinarId );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                responseCallback( false );
            } ).finally( () => loading.value = false );
        }
    }

    /**
     * Saves the member's name and phone. Resolves true when the server accepted them and false otherwise; never rejects.
     * Awaitable so a Save button can stay pending until the answer arrives (UX roadmap #44); `responseCallback` is kept
     * for callers written against the old fire-and-forget shape.
     */
    async function updateUser( firstName: string, lastName: string, phone: string, responseCallback?: ( response: boolean ) => void ): Promise< boolean > {
        loading.value = true;
        let accepted: boolean = false;
        try {
            const response: ApiFetchResponse< UpdateUserResponse > | null = await goFetch< UpdateUserResponse, GraphQlPayload >( {
                endpoint: `user/graphql`,
                authToken: authToken.value,
                payload: {
                    query: `mutation UpdateUser( $input: UpdateUserInput! ) { updateUser( input: $input ) }`,
                    variables: {
                        input: {
                            id: user.value.id,
                            firstName: firstName,
                            lastName: lastName,
                            phone: phone
                        }
                    }
                }
            } );
            accepted = response?.data?.updateUser ?? false;
        } catch ( err: unknown ) {
            logger.error( '[ user ] updateUser failed', err );
            // The member sees "not saved" and the logger is silent in production (UX roadmap #87)
            clientStateManager.queueClientError( err, { request: 'user/graphql UpdateUser' } );
        } finally {
            loading.value = false;
        }

        responseCallback?.( accepted );
        return accepted;
    }

    async function submitOpraAgreement( data: KeyValue[] ): Promise< boolean > {
        try {
            const response =
                await goFetch< FavoriteWebinar, OpraAgreementPayload >( {
                    endpoint: `registrar/service/enroll`,
                    authToken: authToken.value,
                    payload: {
                        serviceName: 'DMI Scanner',
                        answers: data
                    }
                } );

            return null != response?.status && isSuccessState( response.status );
        } catch {
            return false;
        } finally {
            loading.value = false;
        }
    }

    return {
        // ACTIONS
        initializeAsync,
        initializeProfileAsync,
        isFavorited,
        isFavoriteSector,
        loadAccountDetailsAsync,
        loadOpraStatusAsync,
        loadNotificationEnrollmentsAsync,
        reset,
        submitOpraAgreement,
        toggleContentFavoriteStatus,
        togglePortfolioBuilderOnDashboard,
        toggleSectorFavoriteStatus,
        updateUser,
        userId,

        // GETTERS
        hasFavoriteSectors,
        hasSignedOpraAgreement,
        isLoading,
        portfolioBuilderOnDashboard,
        recurlyHostedLink: recurlyProfileLink,
        scannerCredentials,
        sectorColumns,
        user,
        userFullName,
        notificationEnrollments,
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useUserStore, import.meta.hot ) );
}
